/** Why a sign-in stopped; the sign-in page explains each one. */
export type SignInFailure =
  | 'wrong_credentials'
  | 'too_many_attempts'
  | 'invalid_code'
  | 'invalid_password'
  | 'password_reset_required'
  | 'mfa_setup_unavailable'
  | 'access_denied'
  | 'admin_consent_required'
  | 'tenant_not_allowed'
  | 'email_not_verified'
  | 'account_not_found'
  | 'account_disabled'
  | 'session_expired'
  /** The organisation requires assignment to the app and hasn't assigned this user. */
  | 'not_assigned'
  /** The organisation's Conditional Access or tenant restrictions blocked the sign-in. */
  | 'blocked_by_policy'
  /** Cognito or Entra isn't configured for this route yet (an admin's job). */
  | 'sign_in_unavailable'
  /** Cognito stopped the Google sign-in that linked the login; signing in again finishes it. */
  | 'account_linked'
  | 'sign_in_failed';

/**
 * Reasons the Cognito trigger Lambda reports (`MICROSOFT_SIGN_IN:<reason>`),
 * for Microsoft sign-in and, through its Pre sign-up step, for Google.
 */
const LAMBDA_REASONS: readonly SignInFailure[] = [
  'tenant_not_allowed',
  'email_not_verified',
  'account_not_found',
  'account_disabled',
  'sign_in_unavailable',
  'sign_in_failed',
];

export const lambdaReason = (message: string): SignInFailure | undefined => {
  const reason = message.match(/MICROSOFT_SIGN_IN:(\w+)/)?.[1];
  if (!reason) return undefined;
  return LAMBDA_REASONS.find((known) => known === reason) ?? 'sign_in_failed';
};

/**
 * Why Cognito sent a Google sign-in back with an error: the Lambda's reason,
 * or `account_linked` when Cognito stopped the attempt during which the login
 * was linked ("Already found an entry for username …", or an earlier Lambda's
 * "already linked"). The next attempt goes straight in.
 */
export const signInRefusal = (message: string): SignInFailure | undefined =>
  lambdaReason(message) ??
  (/already found an entry for username|already linked/i.test(message)
    ? 'account_linked'
    : undefined);

/** A sign-in that has to stop; `returnTo` is where the user started, when known. */
export class SignInError extends Error {
  constructor(readonly reason: SignInFailure, readonly returnTo?: string) {
    super(reason);
  }
}
